import { formatCurrency } from '../../helper'
// Ingram Micro's flat file has materially fewer fields than TD Synnex's (no TAA,
// no gov pricing, no warehouse-level availability, no promo fields) — the data
// isn't apples-to-apples between distributors yet. This is expected for now.
// When things get normalized, we can remove this comment - DH

export type IngramMicroSftpItem = {
  statusCode: string
  ingramSKU: string
  vendorCode: string
  manufacturerName: string

  description1: string
  description2: string

  listPrice: number | null
  cost: number | null

  mfgPN: string

  weight: number | null
  length: number | null
  width: number | null
  height: number | null

  upcCode: string | null

  unknown14: string | null
  flag1: string
  flag2: string
  unknown18: string | null
  flag3: string

  cpuCode: string
  mediaType: string
  productGroupNumber: string
}

export const INGRAM_MICRO_SECTIONS = [
  'Identity',
  'Description',
  'Pricing',
  'Availability',
  'Physical',
  'Codes',
  'Category',
  'Flags',
] as const

export type IngramMicroSection = (typeof INGRAM_MICRO_SECTIONS)[number]

export type IngramMicroFieldRow = {
  fieldNumber: string
  name: string
  description: string
  section: IngramMicroSection
  accessor: (item: IngramMicroSftpItem) => string
}

const dash = '—'
const text = (v: string | null | undefined) => v || dash
const num = (v: number | null | undefined) => (v === null || v === undefined ? dash : String(v))
const currency = (v: number | null | undefined) => (v === null || v === undefined ? dash : formatCurrency(v))

export const INGRAM_MICRO_FIELD_ROWS: IngramMicroFieldRow[] = [
  // Identity
  {
    fieldNumber: '08',
    name: 'Manufacturer Part #',
    description: "Manufacturer's Part Number (may be truncated to ~20 chars)",
    section: 'Identity',
    accessor: item => text(item.mfgPN),
  },
  {
    fieldNumber: '01',
    name: 'Status Code',
    description: 'A = Active',
    section: 'Identity',
    accessor: item => text(item.statusCode),
  },
  {
    fieldNumber: '02',
    name: 'Ingram SKU',
    description: 'Ingram Micro internal SKU number',
    section: 'Identity',
    accessor: item => text(item.ingramSKU),
  },
  {
    fieldNumber: '03',
    name: 'Vendor Code',
    description: 'Ingram Micro vendor/category code',
    section: 'Identity',
    accessor: item => text(item.vendorCode),
  },
  {
    fieldNumber: '04',
    name: 'Manufacturer Name',
    description: "Manufacturer's brand name",
    section: 'Identity',
    accessor: item => text(item.manufacturerName),
  },

  // Description
  {
    fieldNumber: '05',
    name: 'Description (Part 1)',
    description: 'Product description — first ~31 chars',
    section: 'Description',
    accessor: item => text(item.description1),
  },
  {
    fieldNumber: '06',
    name: 'Description (Part 2)',
    description: 'Product description overflow / continuation',
    section: 'Description',
    accessor: item => text(item.description2),
  },

  // Pricing
  {
    fieldNumber: '15',
    name: 'Customer Price',
    description: 'IMPRES customer price',
    section: 'Pricing',
    accessor: item => currency(item.cost),
  },
  {
    fieldNumber: '16',
    name: 'Special Price',
    description: 'Special or promotional price indicator (meaning unconfirmed)',
    section: 'Pricing',
    accessor: item => text(item.flag1),
  },
  {
    fieldNumber: '07',
    name: 'List Price / MSRP',
    description: "Manufacturer's suggested retail price",
    section: 'Pricing',
    accessor: item => currency(item.listPrice),
  },

  // Availability
  {
    fieldNumber: '17',
    name: 'Availability',
    description: 'Stock availability indicator (meaning unconfirmed)',
    section: 'Availability',
    accessor: item => text(item.flag2),
  },

  // Physical
  {
    fieldNumber: '09',
    name: 'Weight (lbs)',
    description: 'Ship weight in pounds',
    section: 'Physical',
    accessor: item => num(item.weight),
  },
  {
    fieldNumber: '11',
    name: 'Length (in)',
    description: 'Product length in inches',
    section: 'Physical',
    accessor: item => num(item.length),
  },
  {
    fieldNumber: '12',
    name: 'Width (in)',
    description: 'Product width in inches',
    section: 'Physical',
    accessor: item => num(item.width),
  },
  {
    fieldNumber: '13',
    name: 'Height (in)',
    description: 'Product height in inches',
    section: 'Physical',
    accessor: item => num(item.height),
  },

  // Codes
  {
    fieldNumber: '10',
    name: 'UPC Code',
    description: 'Universal Product Code / EAN barcode',
    section: 'Codes',
    accessor: item => text(item.upcCode),
  },

  // Category
  {
    fieldNumber: '19',
    name: 'CPU Code',
    description: 'Ingram Micro detailed product category',
    section: 'Category',
    accessor: item => text(item.cpuCode),
  },
  {
    fieldNumber: '20',
    name: 'Media Type',
    description: 'License, subscription, service or physical media',
    section: 'Category',
    accessor: item => text(item.mediaType),
  },
  {
    fieldNumber: '21',
    name: 'Product Group',
    description: 'Ingram Micro product group number (Categorization.xlsx)',
    section: 'Category',
    accessor: item => text(item.productGroupNumber),
  },

  // Flags (meaning unconfirmed)
  {
    fieldNumber: '14',
    name: 'Unknown 14',
    description: 'Always blank in all observed rows — purpose unknown',
    section: 'Flags',
    accessor: item => text(item.unknown14),
  },
  {
    fieldNumber: '18',
    name: 'Unknown 18',
    description: 'Always blank in all observed rows — purpose unknown',
    section: 'Flags',
    accessor: item => text(item.unknown18),
  },
  {
    fieldNumber: '22',
    name: 'New Item Receipt Flag',
    description: 'Indicates a newly received item',
    section: 'Flags',
    accessor: item => text(item.flag3),
  },
]
